import { TabContext } from "@mui/lab";
import { Box, Menu, MenuItem, Stack } from "@mui/material";
import { useState } from "react";
import { CommsMarkdownContent } from "./comms-markdown-content";
import { CommsTemplateImage } from "./comms-template-image";
import { CommsTemplateTones } from "./comms-template-tones";
import { useGenerateCommsContext } from "./generate-comms-context";
import { AITemplateContentTabPanel } from "./template-content-tab-panel";
import { TemplateSubject } from "./template-subject";

import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { BoxProps } from "@mui/material";
import type { FC, MouseEvent } from "react";

export const CommsTemplateContent: FC<
  {
    contentTone: string;
    onContentToneChange?: (value: ContentTone | "custom") => void;
  } & BoxProps
> = ({
  contentTone,

  onContentToneChange,
  ...boxProps
}) => {
  const {
    format,
    tones: { customTones, selectedTone, setSelectedTone },
    previewImages: { images },
  } = useGenerateCommsContext();

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClick = (event: MouseEvent): void => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  if (!contentTone) {
    return null;
  }

  return (
    <>
      <Box {...boxProps}>
        <TabContext value={contentTone}>
          <Stack sx={{ gap: 2, mt: 2 }}>
            {format === "Communication" ? (
              <TemplateSubject subject={images?.find((x) => x !== undefined)?.data?.subjectLine} />
            ) : null}
            <CommsTemplateImage format={format} />

            <CommsTemplateTones
              // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              contentTone={contentTone as ContentTone}
              onContentToneChange={onContentToneChange}
              selectedBrandTone={selectedTone}
              customBrandTones={customTones}
              handleClick={handleClick}
            />
          </Stack>
          {selectedTone && (
            <AITemplateContentTabPanel
              sx={{ p: 0 }}
              value="custom"
              contentTone="custom"
              title={selectedTone.name}
            >
              <CommsMarkdownContent tone={selectedTone.summary} format={format} />
            </AITemplateContentTabPanel>
          )}

          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="professional"
            contentTone="professional"
            title="Professional"
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="casual"
            contentTone="casual"
            title="Casual"
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
          <AITemplateContentTabPanel
            sx={{ p: 0 }}
            value="direct"
            contentTone="direct"
            title="Direct"
          >
            <CommsMarkdownContent tone={contentTone} format={format} />
          </AITemplateContentTabPanel>
        </TabContext>
      </Box>
      <Menu
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {customTones?.map((tone) => (
          <MenuItem
            key={tone.id}
            selected={tone.id === selectedTone?.id}
            onClick={() => {
              setAnchorEl(null);
              void setSelectedTone?.(tone.id);
            }}
          >
            {tone.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
